<script lang="ts">
	/**
	 * In-room header: muted back IconButton at the start of the room tab rail
	 * (overlaid so the Tabs underline still spans the full width). Room name
	 * sits under the separator. Kebab stays in the Tabs action slot.
	 * On mobile edit, matches dashboard chrome: dissolve tabs, full-width Finish.
	 */
	import { format } from 'svelte-i18n';
	import { location, push } from 'svelte-spa-router';
	import { listHeader } from 'src/stores/list_header';
	import { edit } from 'src/stores/ui/edit_lists';
	import { is_mobile } from 'src/stores/ui/viewport';
	import { editChromeExitDissolving } from 'src/helpers/edit_chrome_helpers';
	import type { HeaderTabsStyle } from 'src/stores/features/dev/header-chrome';
	import ListHeaderActions from 'src/components/Lists/ListHeaderActions.svelte';
	import { Tabs, Tab, IconButton, Breadcrumbs } from '@Shelly/shelly-design-system';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { deriveRoomName, getRoomTabKeys, pushRoomListType } from 'src/helpers/service_rooms_helpers';

	let { useNewLayout = true, tabsStyle }: { useNewLayout?: boolean; tabsStyle: HeaderTabsStyle } =
		$props();

	const roomId = $derived($location.split('/')[3] ?? '');
	const roomListType = $derived($location.split('/')[4] ?? '');
	const tabs = $derived(getRoomTabKeys(roomId));
	const roomName = $derived(deriveRoomName(roomId));
	const isMobileEditChrome = $derived(($edit || $editChromeExitDissolving) && $is_mobile);

	const goBackToRooms = () => {
		const roomBackRoute = '/home/rooms/-1';
		setSidePanelState({
			type: roomBackRoute,
			id: '-1',
		});
		push(roomBackRoute);
	};
</script>

<div class="d-flex flex-direction-column mb-lg-m">
	<div
		class="room-header-rail position-relative width-full"
		class:is-mobile-edit-chrome={isMobileEditChrome}
	>
		{#if !isMobileEditChrome}
			<div class="room-header-back d-flex align-items-center">
				<IconButton
					variant="muted"
					icon="fas fa-chevron-left"
					aria-label={$format('aria_back_room', { default: 'Back to room list' })}
					on:click={goBackToRooms}
				/>
			</div>
		{/if}
		<Tabs
			{useNewLayout}
			{tabsStyle}
			location={tabs.findIndex((tab) => tab === roomListType)}
			hideScrollArrows
			hideSlotAction={!$listHeader}
			hideTabStrip={isMobileEditChrome}
			exitDissolving={$editChromeExitDissolving}
		>
			<svelte:fragment slot="action">
				<ListHeaderActions />
			</svelte:fragment>
			{#each tabs as tab (tab)}
				<Tab
					class="text-capitalize"
					{useNewLayout}
					{tabsStyle}
					active={tab === roomListType}
					hideRightSlot
					on:click={(event) => {
						event.preventDefault();
						pushRoomListType(roomId, tab);
					}}
				>
					{$format(`tab_${tab}`, { default: tab })}
				</Tab>
			{/each}
		</Tabs>
	</div>
	{#if roomName && !isMobileEditChrome}
		<div class="room-header-name mt-m">
			<Breadcrumbs
				aria-label={$format('wizard_step_room', { default: 'Room' })}
				links={[{ label: roomName }]}
				listClasses="width-full"
			/>
		</div>
	{/if}
</div>

<style>
	.room-header-back {
		position: absolute;
		left: 0;
		top: 0;
		z-index: 4;
		height: 44px;
	}

	/* Tabs draws ::after on .tabs-wrapper; padding keeps labels clear of back. */
	.room-header-rail:not(.is-mobile-edit-chrome) :global(.tabs-wrapper) {
		padding-left: calc(32px + var(--spacing-s));
	}

	/*
	 * staticTop is 15px from the right; list cards sit in .scroll.new-layout
	 * which adds extra right padding (scrollbar gutter). Match that so the
	 * name row lines up with the entries.
	 */
	.room-header-name {
		margin-right: 9px;
	}

	@media screen and (max-width: 1123px) {
		.room-header-name {
			margin-right: 15px;
		}
	}

	@media screen and (max-width: 639px) {
		.room-header-name {
			margin-right: 5px;
		}
	}
</style>
